
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import {
  AddIcon,
  ChevronDownIcon,
  DownloadIcon,
  EmailIcon,
  EditIcon,
  SearchIcon,
  ViewIcon,
} from "@chakra-ui/icons";
import Card from "components/card/Card";
import { Invoice, invoiceRepository, InvoiceForm } from "api/invoice";
import { customerRepository, CustomerIdNameAddressPlant } from "api/customer";
import { useAuth } from "contexts/authContext";
import { getMasterDataValues, settingRepository } from "api/setting";
import AddInvoiceDrawer from "./addinvoice";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

/* ---------- Palette (taken from the dashboard screenshot) ---------- */
const PURPLE = "#7062B9";
const PURPLE_DARK = "#5B4FA3";
const PURPLE_SOFT = "#EEEBFA";
const NAVY = "#2D2A5E";
const MUTED = "#9A98B8";
const TEAL = "#2BA8A0";
const RED = "#E5484D";

const HEADER_GRADIENT = `linear-gradient(90deg, ${PURPLE_DARK}, ${PURPLE})`;

const primaryBtn = {
  bg: PURPLE,
  color: "white",
  _hover: { bg: PURPLE_DARK },
  _active: { bg: PURPLE_DARK },
};

const emptyForm = (): InvoiceForm => ({
  customerId: "",
  customerName: "",
  location: "",
  type: "",
  equipmentName: "",
  noOfVisit: 0,
  kindlyAttend: "",
  reference: "",
  subject: "",
  plant: "",
  serviceAmount: "",
  supportAmount: "",
});

const QUOTATION_API_PAGE_SIZE = 100;


interface CustomerGroup {
  customerId: string;
  customerName: string;
  location: string;
  count: number;
}

export default function InvoicePage() {
  const userId = useAuth();

  // Theme tokens
  const pageBg = useColorModeValue("#F0F2FF", "#0B1437");
  const cardBg = useColorModeValue("white", "#111C44");
  const rowBg = useColorModeValue("white", "#1B254B");
  const rowAltBg = useColorModeValue("#FAF9FF", "#18224A");
  const tableBg = useColorModeValue("gray.50", "#1B254B");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const hoverBg = useColorModeValue(PURPLE_SOFT, "whiteAlpha.100");
  const textColor = useColorModeValue(NAVY, "white");
  const mutedColor = useColorModeValue(MUTED, "whiteAlpha.600");
  const rowShadow = useColorModeValue(
    "0 1px 3px rgba(112,98,185,0.10)",
    "0 1px 3px rgba(0,0,0,0.4)"
  );
  const btncolor = useColorModeValue(NAVY, "white");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const [search, setSearch] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState("");
  const [centerAlertStatus, setCenterAlertStatus] = useState<"success" | "error" | "warning" | "info">("success");
  const [downloadingReportId, setDownloadingReportId] = useState("");
  const [previewingReportId, setPreviewingReportId] = useState("");
  const [sendingReportEmailId, setSendingReportEmailId] = useState("");
  const [quotationPreviewUrl, setQuotationPreviewUrl] = useState<string | null>(null);
  const [previewedQuotationId, setPreviewedQuotationId] = useState("");
  const quotationPreviewUrlRef = useRef<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<CustomerIdNameAddressPlant[]>([]);
  const [quotationTypeOptions, setQuotationTypeOptions] = useState<string[]>([]);
  const [referenceOptions, setReferenceOptions] = useState<string[]>([]);
  const [subjectOptions, setSubjectOptions] = useState<string[]>([]);
  // all quotations, used only to build the customer grouping for the main table
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  // the customer currently open in the View modal, and their quotations
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerGroup | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
  const [openEditAfterViewClose, setOpenEditAfterViewClose] = useState(false);
  const [form, setForm] = useState<InvoiceForm>(emptyForm());
  const centerAlertTimerRef = React.useRef<number | null>(null);
  const invoiceLoadIdRef = useRef(0);

  const showAlert = (status: "success" | "error" | "warning" | "info", message: string) => {
    setCenterAlertStatus(status);
    setCenterAlertMessage(message);
    setCenterAlertVisible(true);

    if (centerAlertTimerRef.current) {
      clearTimeout(centerAlertTimerRef.current);
    }

    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
    }, 2000);
  };

  /* ---------- Data loading ---------- */

  const loadAllInvoices = async () => {
    const loadId = ++invoiceLoadIdRef.current;
    setLoading(true);
    try {
      const firstPage = await invoiceRepository.getinvoice(
        1,
        QUOTATION_API_PAGE_SIZE
      );
      const pageCount =
        firstPage.totalPages ??
        Math.ceil(firstPage.totalCount / QUOTATION_API_PAGE_SIZE);
      const remainingPages = await Promise.all(
        Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) =>
          invoiceRepository.getinvoice(index + 2, QUOTATION_API_PAGE_SIZE)
        )
      );

      if (loadId !== invoiceLoadIdRef.current) return;

      setAllInvoices(
        [firstPage, ...remainingPages]
          .flatMap((page) => page.data)
          .filter((invoice) => !invoice.isDeleted)
      );
    } catch (err) {
      if (loadId === invoiceLoadIdRef.current) {
        showAlert("error", "Unable to load quotations");
      }
    } finally {
      if (loadId === invoiceLoadIdRef.current) {
        setLoading(false);
      }
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await customerRepository.getCustomerIdNameAddressPlant();
      setCustomers(Array.isArray(response) ? response : []);
    } catch (err) {
      showAlert("error", "Unable to load customers");
    }
  };

  const loadQuotationOptions = async () => {
    try {
      const masterData = await settingRepository.getSetting(0, 1000, "");
      const loadValues = async (aliases: string[]) => {
        try {
          return await getMasterDataValues(aliases, masterData);
        } catch (error) {
          console.error(`Unable to load master data category "${aliases[0]}":`, error);
          return [];
        }
      };
      const [types, references, subjects] = await Promise.all([
        loadValues(["Type", "Types", "Quotation Type", "Quotation Types", "Quotation"]),
        loadValues(["Reference", "References", "Quotation Reference"]),
        loadValues(["Subject", "Subjects", "Quotation Subject"]),
      ]);
      setQuotationTypeOptions(types);
      setReferenceOptions(references);
      setSubjectOptions(subjects);
    } catch (error) {
      console.error("Unable to load quotation master data:", error);
      showAlert("error", "Unable to load quotation dropdown options");
    }
  };

  useEffect(() => {
    loadCustomers();
    loadAllInvoices();
    loadQuotationOptions();
  }, []);

  useEffect(() => {
    if (openEditAfterViewClose && !isViewOpen) {
      setOpenEditAfterViewClose(false);
      onOpen();
    }
  }, [openEditAfterViewClose, isViewOpen, onOpen]);

  /* ---------- Group invoices by customer for the main table ---------- */

  const customerGroups: CustomerGroup[] = React.useMemo(() => {
    const map = new Map<string, CustomerGroup>();

    allInvoices.forEach((inv) => {
      if (!inv.customerId) return;

      const existing = map.get(inv.customerId);

      if (existing) {
        existing.count += 1;
      } else {
        map.set(inv.customerId, {
          customerId: inv.customerId,
          customerName: inv.customerName,
          location: inv.location,
          count: 1,
        });
      }
    });

    return Array.from(map.values());
  }, [allInvoices]);

  const filteredGroups = customerGroups.filter(
    (g) =>
      g.customerName.toLowerCase().includes(search.toLowerCase()) ||
      (g.location || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalCount = filteredGroups.length;
  const pageStart = pageIndex * pageSize;
  const pagedGroups = filteredGroups.slice(pageStart, pageStart + pageSize);
  const hasMore = pageStart + pageSize < totalCount;

  const createPages = () => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  /* ---------- View modal ---------- */

  const handleViewCustomer = async (group: CustomerGroup) => {
    setSelectedCustomer(group);
    onViewOpen();

    try {
      const list = await invoiceRepository.getbycustomer(group.customerId);
      setSelectedInvoices(Array.isArray(list) ? list.filter((q) => !q.isDeleted) : []);
    } catch (err) {
      showAlert("error", "Unable to load this customer's quotations");
      setSelectedInvoices([]);
    }
  };

  const refreshSelectedCustomer = async () => {
    if (!selectedCustomer) return;

    try {
      const list = await invoiceRepository.getbycustomer(selectedCustomer.customerId);
      setSelectedInvoices(Array.isArray(list) ? list.filter((q) => !q.isDeleted) : []);
    } catch (err) {
      // silent - main list reload below will still reflect changes
    }
  };

  /* ---------- Form handlers ---------- */

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [id]:
        id === "noOfVisit" || id === "serviceAmount" || id === "supportAmount"
          ? value === ""
            ? ""
            : Number(value)
          : value,
    }));
  };

  const handleCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const customerId = e.target.value;
    const selected = customers.find((c) => c.id === customerId);

    setForm((prev) => ({
      ...prev,
      customerId,
      customerName: selected?.customerName || "",
      location: selected?.address || "",
      plant: selected?.plant || "",
      equipmentName: selected?.equipmentName || "",
    }));
  };

  const handleQuotationFieldChange = (
    field: "reference" | "subject",
    value: string
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setForm(emptyForm());
    setIsEdit(false);
    setEditingId("");
  };

  const handleAdd = () => {
    handleReset();
    onOpen();
  };

  const handleEdit = (item: Invoice) => {
    setIsEdit(true);
    setEditingId(item.id);

    setForm({
      customerId: item.customerId || "",
      customerName: item.customerName || "",
      location: item.location || "",
      type: item.type || "Service",
      equipmentName: item.equipmentName || "",
      noOfVisit: item.noOfVisit ?? 0,
      kindlyAttend: item.kindlyAttend || "",
      reference: item.reference || "",
      subject: item.subject || "",
      plant: item.plant || "",
      serviceAmount: item.serviceAmount ?? 0,
      supportAmount: item.supportAmount ?? 0,
    });

    if (isViewOpen) {
      setOpenEditAfterViewClose(true);
      onViewClose();
    } else {
      onOpen();
    }
  };

  const handleSubmit = async () => {
    try {
      if (!form.customerId) {
        showAlert("warning", "Please select a customer");
        return;
      }

      const payload = {
        customerId: form.customerId,
        customerName: form.customerName,
        location: form.location,
        type: form.type,
        equipmentName: form.equipmentName,
        noOfVisit: Number(form.noOfVisit) || 0,
        kindlyAttend: form.kindlyAttend,
        reference: form.reference,
        subject: form.subject,
        plant: form.plant,
        serviceAmount: Number(form.serviceAmount) || 0,
        supportAmount: Number(form.supportAmount) || 0,
      };

      if (isEdit) {
        const existing = allInvoices.find((x) => x.id === editingId);

        const fullPayload: Invoice = {
          ...(existing as Invoice),
          ...payload,
          modifiedAt: new Date().toISOString(),
        };

        await invoiceRepository.updateinvoice(editingId, fullPayload);

        showAlert("success", "Quotation Updated Successfully");
      } else {
        await invoiceRepository.postinvoice(payload);

        showAlert("success", "Quotation Added Successfully");
      }

      handleReset();
      onClose();

      await loadAllInvoices();
      await refreshSelectedCustomer();
    } catch (error) {
      console.error(error);
      showAlert("error", "Unable to save quotation");
    }
  };

  const handleDownloadReport = async (invoice: Invoice) => {
    setDownloadingReportId(invoice.id);
    try {
      const { blob, fileName } = await invoiceRepository.getQuotationReport(invoice.id);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName || `quotation-${invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
    } catch (error) {
      showAlert("error", "Unable to download quotation report");
    } finally {
      setDownloadingReportId("");
    }
  };

  const clearQuotationPreview = () => {
    if (quotationPreviewUrlRef.current) {
      URL.revokeObjectURL(quotationPreviewUrlRef.current);
      quotationPreviewUrlRef.current = null;
    }
    setQuotationPreviewUrl(null);
    setPreviewedQuotationId("");
  };

  const handlePreviewReport = async (invoice: Invoice) => {
    setPreviewingReportId(invoice.id);
    try {
      const { blob } = await invoiceRepository.getQuotationReport(invoice.id);
      clearQuotationPreview();
      const pdfBlob = blob.type === "application/pdf"
        ? blob
        : new Blob([blob], { type: "application/pdf" });
      const previewUrl = URL.createObjectURL(pdfBlob);
      quotationPreviewUrlRef.current = previewUrl;
      setQuotationPreviewUrl(previewUrl);
      setPreviewedQuotationId(invoice.id);
    } catch (error) {
      console.error("Unable to preview quotation report:", error);
      showAlert("error", "Unable to preview quotation report");
    } finally {
      setPreviewingReportId("");
    }
  };

  const handleSendQuotationEmail = async () => {
    if (!previewedQuotationId) {
      showAlert("error", "Quotation ID not found.");
      return;
    }

    setSendingReportEmailId(previewedQuotationId);
    try {
      await invoiceRepository.sendQuotationReportEmail(previewedQuotationId);
      showAlert("success", "Quotation email sent successfully.");
    } catch (error: any) {
      console.error("Unable to send quotation report email:", error);
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : "") ||
        "Unable to send quotation report email.";
      showAlert("error", message);
    } finally {
      setSendingReportEmailId("");
    }
  };

  useEffect(
    () => () => {
      if (quotationPreviewUrlRef.current) {
        URL.revokeObjectURL(quotationPreviewUrlRef.current);
      }
    },
    []
  );

  const thStyle = {
    color: mutedColor,
    fontWeight: 500,
    fontSize: "12px",
    textTransform: "none" as const,
    letterSpacing: "0.02em",
    borderBottom: "none",
    py: 2,
  };

  const tdStyle = {
    border: "none",
    py: 4,
    fontSize: "14px",
    color: textColor,
    transition: "background 0.15s ease, color 0.15s ease",
  };

  return (
    <>
      {centerAlertVisible && (
        <Center position="fixed" inset={0} bg="blackAlpha.300" zIndex={9999}>
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="550px"
            borderRadius="18px"
            py={8}
            px={8}
            boxShadow="2xl"
          >
            <AlertIcon boxSize="44px" mr={0} />
            <Text mt={3} fontWeight="bold" fontSize="lg">
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}

      <Flex mt="70px" p={2} bg={pageBg} borderRadius="25px">
        <Card bg={cardBg} borderRadius="25px" overflow="hidden" boxShadow="0 10px 30px rgba(112,98,185,0.12)" w="100%">
          {/* ---------- Header: search and add ---------- */}
          <Flex p={6} justify="flex-end" align="center" gap={4} wrap="wrap">
            <HStack spacing={3}>
              <InputGroup maxW="320px">
                <InputLeftElement h="44px" pointerEvents="none">
                  <SearchIcon color={PURPLE} />
                </InputLeftElement>
                <Input
                  h="44px"
                  borderRadius="14px"
                  bg={PURPLE_SOFT}
                  border="none"
                  color={NAVY}
                  _placeholder={{ color: MUTED }}
                  _focus={{ boxShadow: `0 0 0 2px ${PURPLE}` }}
                  placeholder="Search customer or location..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPageIndex(0);
                  }}
                />
              </InputGroup>

              <Button
                leftIcon={<AddIcon boxSize="3" />}
                h="50px"
                minW="max-content"
                px={6}
                fontSize="md"
                whiteSpace="nowrap"
                flexShrink={0}
                borderRadius="14px"
                {...primaryBtn}
                boxShadow="0 6px 14px rgba(112,98,185,0.35)"
                onClick={handleAdd}
              >
                Add Quotation
              </Button>
            </HStack>
          </Flex>

          {/* ---------- Table: one row per customer ---------- */}
          <Box px={6} pb={2} overflowX="auto">
            <Table
              variant="unstyled"
              w="100%"
              sx={{ borderCollapse: "separate", borderSpacing: "0 10px" }}
            >
              <Thead>
                <Tr>
                  <Th {...thStyle} w="40%">Customer</Th>
                  <Th {...thStyle} w="45%">Location</Th>
                  <Th {...thStyle} w="15%" textAlign="center">View Quotations</Th>
                </Tr>
              </Thead>

              <Tbody>
                {pagedGroups.length === 0 ? (
                  <Tr>
                    <Td colSpan={3} textAlign="center" py={10} border="none">
                      <Text color={mutedColor}>
                        {loading ? "Loading..." : search.trim() ? "No customers found for your search." : "No quotations found"}
                        {search.trim() ? "No customers found for your search." : "No quotations found"}
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  pagedGroups.map((group, index) => {
                    const base = index % 2 === 0 ? rowBg : rowAltBg;

                    return (
                      <Tr
                        key={group.customerId}
                        cursor="pointer"
                        onClick={() => handleViewCustomer(group)}
                        sx={{
                          "& > td": { bg: base, boxShadow: rowShadow },
                          "& > td:first-of-type": { borderLeftRadius: "14px" },
                          "& > td:last-of-type": { borderRightRadius: "14px" },
                        }}
                        _hover={{
                          "& > td": {
                            bg: `linear-gradient(90deg, ${PURPLE} 0%, #8A7DD0 100%)`,
                            color: "white",
                          },
                          "& .row-action": { color: "white" },
                        }}
                      >
                        <Td {...tdStyle}>
                          <HStack spacing={3}>
                            <Avatar size="sm" name={group.customerName} bg={PURPLE_SOFT} color={PURPLE} />
                            <Text fontWeight="600">{group.customerName}</Text>
                          </HStack>
                        </Td>

                        <Td {...tdStyle}>
                          <Text whiteSpace="normal" wordBreak="break-word" maxW="320px">
                            {group.location || "-"}
                          </Text>
                        </Td>

                        <Td {...tdStyle} textAlign="center">
                          <IconButton
                            className="row-action"
                            icon={<ViewIcon />}
                            aria-label="View quotations"
                            size="sm"
                            variant="ghost"
                            color={PURPLE}
                            _hover={{ bg: "whiteAlpha.300" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCustomer(group);
                            }}
                          />
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          {/* ---------- Footer: results label, page size, pagination ---------- */}
          <Flex w="100%" justify="space-between" align="center" px={6} pb={6} pt={2} wrap="wrap" gap={3}>
            <HStack spacing={3}>
              <Text fontSize="13px" color={mutedColor}>
                {totalCount > 0
                  ? `${pageStart + 1}-${Math.min(pageStart + pageSize, totalCount)} of ${totalCount} results`
                  : "0 results"}
              </Text>

              <Menu>
                <Text fontSize="13px" color={mutedColor}>Rows per page</Text>
                <MenuButton
                  as={Button}
                  size="sm"
                  rightIcon={<ChevronDownIcon />}
                  bg={PURPLE_SOFT}
                  color={PURPLE}
                  borderRadius="10px"
                  _hover={{ bg: "#E2DDF6" }}
                  _active={{ bg: "#E2DDF6" }}
                >
                  {pageSize}
                </MenuButton>
                <MenuList zIndex={1000} boxShadow="lg" borderRadius="12px">
                  {[10, 20].map((size) => (
                    <MenuItem
                      key={size}
                      onClick={() => {
                        setPageSize(size);
                        setPageIndex(0);
                      }}
                    >
                      {size}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </HStack>

            <HStack spacing={1}>
              <IconButton
                aria-label="Previous page"
                icon={<MdChevronLeft size={18} />}
                variant="ghost"
                size="sm"
                color={btncolor}
                isDisabled={pageIndex === 0}
                onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
              />

              {createPages().map((num) => {
                const active = num === pageIndex + 1;
                return (
                  <Button
                    key={num}
                    size="sm"
                    minW="32px"
                    h="32px"
                    borderRadius="10px"
                    fontWeight={active ? 700 : 500}
                    bg={active ? PURPLE : "transparent"}
                    color={active ? "white" : btncolor}
                    _hover={{ bg: active ? PURPLE_DARK : PURPLE_SOFT }}
                    onClick={() => setPageIndex(num - 1)}
                  >
                    {num}
                  </Button>
                );
              })}

              <IconButton
                aria-label="Next page"
                icon={<MdChevronRight size={18} />}
                variant="ghost"
                size="sm"
                color={btncolor}
                isDisabled={!hasMore}
                onClick={() => setPageIndex((prev) => prev + 1)}
              />
            </HStack>
          </Flex>
        </Card>

        <AddInvoiceDrawer
          isOpen={isOpen}
          onClose={onClose}
          isEdit={isEdit}
          form={form}
          customers={customers}
          typeOptions={quotationTypeOptions}
          referenceOptions={referenceOptions}
          subjectOptions={subjectOptions}
          handleChange={handleChange}
          handleQuotationFieldChange={handleQuotationFieldChange}
          handleCustomerChange={handleCustomerChange}
          handleSubmit={handleSubmit}
          handleReset={handleReset}
        />

        {/* ===================== VIEW CUSTOMER INVOICES ===================== */}

        <Modal
          isOpen={isViewOpen}
          onClose={() => {
            clearQuotationPreview();
            onViewClose();
          }}
          size={quotationPreviewUrl ? "6xl" : "4xl"}
          isCentered
          scrollBehavior="inside"
        >
          <ModalOverlay bg="blackAlpha.500" />

          <ModalContent
            borderRadius="20px"
            overflow="hidden"
            h={quotationPreviewUrl ? "90vh" : undefined}
            maxW={quotationPreviewUrl ? "90vw" : undefined}
          >
            <ModalHeader bg={HEADER_GRADIENT} color="white">
              {quotationPreviewUrl ? (
                <Flex align="center" gap={3} pr={8}>
                  <Button
                    size="sm"
                    variant="outline"
                    color="white"
                    borderColor="whiteAlpha.700"
                    leftIcon={<MdChevronLeft />}
                    onClick={clearQuotationPreview}
                  >
                    Back to quotations
                  </Button>
                  <Text fontSize="xl" fontWeight="700">Quotation Report Preview</Text>
                </Flex>
              ) : (
                <>
                  <Text fontSize="xl" fontWeight="700">Quotation Details</Text>
                  <Text fontSize="sm" fontWeight="normal" opacity={0.9} mt={1}>
                    {selectedCustomer?.customerName || "Customer"}
                  </Text>
                </>
              )}
            </ModalHeader>

            <ModalCloseButton color="white" />

            <ModalBody
              py={quotationPreviewUrl ? 0 : 6}
              px={quotationPreviewUrl ? 0 : 6}
              display="flex"
              flexDirection="column"
              minH={quotationPreviewUrl ? 0 : undefined}
              flex={quotationPreviewUrl ? 1 : undefined}
              bg={quotationPreviewUrl ? "gray.100" : undefined}
            >
              {quotationPreviewUrl ? (
                <iframe
                  title="Quotation Report PDF Preview"
                  src={quotationPreviewUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0, flex: 1, minHeight: 0 }}
                />
              ) : (
                <>
              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mb={6}>
                <Box p={4} border="1px solid" borderColor={border} borderRadius="12px" bg={tableBg}>
                  <Text fontSize="xs" color="gray.500" mb={1}>Customer Name</Text>
                  <Text fontSize="md" fontWeight="600" color={textColor}>
                    {selectedCustomer?.customerName || "-"}
                  </Text>
                </Box>

                <Box p={4} border="1px solid" borderColor={border} borderRadius="12px" bg={tableBg}>
                  <Text fontSize="xs" color="gray.500" mb={1}>Location</Text>
                  <Text fontSize="md" fontWeight="600" color={textColor}>
                    {selectedCustomer?.location || "-"}
                  </Text>
                </Box>
              </Grid>

              <Text fontSize="lg" fontWeight="700" mb={3} color={textColor}>
                Quotations
              </Text>

              <Box border="1px solid" borderColor={border} borderRadius="12px" overflowX="auto">
                <Table variant="simple">
                  <Thead bg={PURPLE}>
                    <Tr>
                      <Th color="white">#</Th>
                      <Th color="white">Type</Th>
                      <Th color="white">Equipment</Th>
                      <Th color="white" isNumeric>Visits</Th>
                      <Th color="white" isNumeric>Service Amount</Th>
                      <Th color="white">Created By</Th>
                      <Th color="white" textAlign="center">Action</Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {selectedInvoices.length > 0 ? (
                      selectedInvoices.map((item, index) => (
                        <Tr key={item.id || index} _hover={{ bg: hoverBg }}>
                          <Td>{index + 1}</Td>

                          <Td>
                            <Text fontSize="sm">{item.type || "-"}</Text>
                          </Td>

                          <Td>
                            <Text fontSize="sm">{item.equipmentName || "-"}</Text>
                          </Td>

                          <Td isNumeric>{item.noOfVisit ?? 0}</Td>
                          <Td isNumeric fontWeight="700">₹ {item.serviceAmount ?? 0}</Td>

                          <Td>
                            <Text fontSize="sm">{item.createdBy || "-"}</Text>
                          </Td>

                          <Td borderColor={border} textAlign="center">
                            <HStack spacing={1} justify="center">
                              <IconButton
                                aria-label="Edit quotation"
                                icon={<EditIcon />}
                                color={PURPLE}
                                variant="ghost"
                                rounded="full"
                                _hover={{ bg: PURPLE_SOFT }}
                                onClick={() => handleEdit(item)}
                              />

                              <Tooltip label="Preview quotation report" hasArrow>
                                <IconButton
                                  aria-label="Preview quotation report"
                                  icon={<ViewIcon />}
                                  colorScheme="purple"
                                  variant="ghost"
                                  rounded="full"
                                  isLoading={previewingReportId === item.id}
                                  onClick={() => handlePreviewReport(item)}
                                />
                              </Tooltip>
                              <Tooltip label="Download quotation report" hasArrow>
                                <IconButton
                                  aria-label="Download quotation report"
                                  icon={<DownloadIcon />}
                                  color={PURPLE}
                                  variant="ghost"
                                  rounded="full"
                                  isLoading={downloadingReportId === item.id}
                                  _hover={{ bg: PURPLE_SOFT }}
                                  onClick={() => handleDownloadReport(item)}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={7} textAlign="center" py={8}>
                          <Text color="gray.500">No quotations found.</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
                </>
              )}
            </ModalBody>

            <ModalFooter borderTop="1px solid" borderColor={border}>
              {quotationPreviewUrl ? (
                <HStack spacing={3}>
                  <Button
                    borderRadius="12px"
                    variant="outline"
                    leftIcon={<EmailIcon />}
                    onClick={handleSendQuotationEmail}
                    isLoading={sendingReportEmailId === previewedQuotationId}
                    isDisabled={!previewedQuotationId}
                  >
                    Send to Email
                  </Button>
                  <Button
                    borderRadius="12px"
                    {...primaryBtn}
                    onClick={clearQuotationPreview}
                  >
                    Back to quotations
                  </Button>
                </HStack>
              ) : (
                <Button
                  borderRadius="12px"
                  {...primaryBtn}
                  onClick={() => {
                    clearQuotationPreview();
                    onViewClose();
                  }}
                >
                  Close
                </Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>

      </Flex>
    </>
  );
}